/**
 * @file stateManager.js
 * @description Centralized state management with Router capabilities.
 * (FIXED: Restored setState method)
 */

import EventBus from '../utils/eventBus.js';

class StateManager {
  constructor() {
    // ✅ ADD: Mobile detection
    const isMobile = this.detectMobile();
    
    this.state = {
        mode: 'GUI',
        theme: 'aegis',
        isMobile: isMobile,  // ✅ ADD THIS
        
        // ========== ROUTER STATE ==========
        path: ['home'],
        
        user: {
            authenticated: false,
            username: 'user'
        },
        ui: {
            statusBarVisible: true,
            sidebarOpen: !isMobile,  // ✅ Start closed on mobile
            reducedMotion: false,
            view: 'gui'
        }
    };
    
    // Bind methods
    this.handlePopState = this.handlePopState.bind(this);
}

// ✅ ADD: Detection method
detectMobile() {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
    const isSmallScreen = window.innerWidth <= 768;
    
    return isMobileUA || isSmallScreen;
}


  /**
   * Initialize the State Manager.
   * Parses the current browser URL to set the initial state.
   */
  init() {
    window.addEventListener('popstate', this.handlePopState);
    
    // Check for deep link
    const initialPath = this.parseHash(window.location.hash);
    if (initialPath.length > 0) {
      console.log(`[Router] Deep link detected: ${initialPath.join('/')}`);
      this.navigate(initialPath, true); 
    }
  }
  
  // ========================================================
  // ROUTER LOGIC
  // ========================================================

  navigate(pathSegments, replace = false) {
    if (!Array.isArray(pathSegments) || pathSegments.length === 0) return;

    const oldPath = this.state.path;
    const newPath = pathSegments;

    // 1. Update Internal State
    this.state.path = newPath;

    // 2. Sync Browser URL
    const hash = '#/' + newPath.join('/');
    if (replace) {
      history.replaceState(null, '', hash);
    } else {
      history.pushState(null, '', hash);
    }

    // 3. Emit Events
    EventBus.emit('state:path:changed', { old: oldPath, new: newPath });

    // Sync legacy UI state
    const section = newPath[0];
    this.set('ui.view', section === 'terminal' ? 'terminal' : 'gui');
    EventBus.emit('page:navigate', { section: section, id: newPath[1] || null });
  }

  handlePopState() {
    const path = this.parseHash(window.location.hash);
    const oldPath = this.state.path;
    this.state.path = path.length ? path : ['home'];
    
    EventBus.emit('state:path:changed', { old: oldPath, new: this.state.path });

    const section = this.state.path[0];
    EventBus.emit('page:navigate', { 
      section: section, 
      id: this.state.path[1] || null 
    });
  }

  parseHash(hash) {
    if (!hash) return ['home'];
    const clean = hash.replace(/^#\/?/, '');
    if (!clean) return ['home'];
    return clean.split('/');
  }

  // ========================================================
  // STANDARD GETTERS / SETTERS
  // ========================================================

  getState() {
    return JSON.parse(JSON.stringify(this.state));
  }

  get(key) {
    const keys = key.split('.');
    let value = this.state;
    for (const k of keys) {
      value = value?.[k];
      if (value === undefined) return null;
    }
    return value;
  }

  /**
   * Updates specific nested key using dot notation.
   * e.g. set('ui.view', 'terminal')
   */
  set(key, value) {
    const keys = key.split('.');
    const lastKey = keys.pop();
    let target = this.state;
    
    for (const k of keys) {
      if (!target[k]) target[k] = {};
      target = target[k];
    }
    
    const oldValue = target[lastKey];
    target[lastKey] = value;
    
    EventBus.emit(`state:${key}:changed`, {
      old: oldValue,
      new: value
    });
  }

  /**
   * Merges an object of updates into the state.
   * REQUIRED for core.js compatibility.
   */
  setState(updates, silent = false) {
    const oldState = this.getState();
    this.state = {
      ...this.state,
      ...updates
    };
    
    if (!silent) {
      EventBus.emit('state:changed', {
        old: oldState,
        new: this.getState(),
        updates
      });
      
      for (const key of Object.keys(updates)) {
        EventBus.emit(`state:${key}:changed`, {
          old: oldState[key],
          new: this.state[key]
        });
      }
    }
  }

  setTheme(themeName) {
      this.set('theme', themeName);
  }


  subscribe(key, callback) {
    return EventBus.on(`state:${key}:changed`, ({ old, new: newVal }) => {
      callback(newVal, old);
    });
  }
}

// Create singleton instance
const StateManagerInstance = new StateManager();
export default StateManagerInstance;